import React from 'react';
import PropTypes from 'prop-types';
import {
    Card,
    CardContent,
    Typography,
    Grid,
    useTheme,
} from '@mui/material';

export const LIFECYCLE_LABELS = {
    active: 'Active',
    scheduled: 'Scheduled',
    expired: 'Expired',
    draft: 'Draft',
};

export const LIFECYCLE_COLORS = {
    active: { light: '#2e7d32', dark: '#66bb6a' },
    scheduled: { light: '#1565c0', dark: '#64b5f6' },
    expired: { light: '#757575', dark: null },
    draft: { light: '#ef6c00', dark: '#ffa726' },
};

export const LIFECYCLE_CARD_BACKGROUNDS = {
    active: '#f1f8e9',
    scheduled: '#e3f2fd',
    expired: '#f5f5f5',
    draft: '#fff3e0',
};

export const getLifecycleColor = (status, isDark, theme) => {
    const colors = LIFECYCLE_COLORS[status];

    if (isDark && status === 'expired') {
        return theme.palette.text.secondary;
    }

    return isDark ? colors.dark : colors.light;
};

const toStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const getLifecycleStatus = (record) => {
    const now = new Date();
    const today = toStartOfDay(now);
    const pub = record.published_at
        ? new Date(record.published_at)
        : (record.effective_from ? new Date(record.effective_from) : null);
    const exp = record.expired_at ? new Date(record.expired_at) : null;
    const effectiveUntil = record.effective_until ? toStartOfDay(new Date(record.effective_until)) : null;
    const archivedAt = record.archived_at ? toStartOfDay(new Date(record.archived_at)) : null;

    if (record.status === 'draft') {
        return 'draft';
    }

    if (pub && pub > now) {
        return 'scheduled';
    }

    if (exp && exp <= now) {
        return 'expired';
    }

    if (effectiveUntil && effectiveUntil < today) {
        return 'expired';
    }

    if (archivedAt && archivedAt < today) {
        return 'expired';
    }

    return 'active';
};

export const computeLifecycleStats = (records = []) => {
    const stats = { active: 0, scheduled: 0, expired: 0, draft: 0 };

    records.forEach((record) => {
        stats[getLifecycleStatus(record)]++;
    });

    return stats;
};

export const StatsHeader = ({ stats }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const cardStyles = Object.fromEntries(
        Object.keys(LIFECYCLE_COLORS).map((status) => [
            status,
            {
                borderLeft: `6px solid ${LIFECYCLE_COLORS[status].light}`,
                bgcolor: isDark ? theme.palette.background.paper : LIFECYCLE_CARD_BACKGROUNDS[status],
            },
        ])
    );

    const valueColors = Object.fromEntries(
        Object.keys(LIFECYCLE_COLORS).map((status) => [
            status,
            getLifecycleColor(status, isDark, theme),
        ])
    );

    return (
        <Grid container spacing={2} sx={{ width: '100%', mb: 3 }}>
            <Grid size={{ xs: 12, sm: 3 }}>
                <Card sx={{ ...cardStyles.active, width: '100%' }} elevation={2}>
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Typography gutterBottom color="text.secondary" variant="subtitle2">
                            Active
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: valueColors.active }}>
                            {stats.active}
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
                <Card sx={{ ...cardStyles.scheduled, width: '100%' }} elevation={2}>
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Typography gutterBottom color="text.secondary" variant="subtitle2">
                            Scheduled
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: valueColors.scheduled }}>
                            {stats.scheduled}
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
                <Card sx={{ ...cardStyles.expired, width: '100%' }} elevation={2}>
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Typography gutterBottom color="text.secondary" variant="subtitle2">
                            Expired
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: valueColors.expired }}>
                            {stats.expired}
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
                <Card sx={{ ...cardStyles.draft, width: '100%' }} elevation={2}>
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Typography gutterBottom color="text.secondary" variant="subtitle2">
                            Draft
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: valueColors.draft }}>
                            {stats.draft}
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

StatsHeader.propTypes = {
    stats: PropTypes.shape({
        active: PropTypes.number.isRequired,
        scheduled: PropTypes.number.isRequired,
        expired: PropTypes.number.isRequired,
        draft: PropTypes.number.isRequired,
    }).isRequired,
};
